import {
  Container,
  Divider,
  Button,
  Grid,
  GridItem,
  Spinner,
  Box,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import useOrganizationStatus from '../../../hooks/useOrganisationStatus';
import EmptyDefaultSvgAnimated from '../../../theme/illustrations-animated/EmptyDefaultSvgAnimated';
import EmptyDefaultDarkSvgAnimated from '../../../theme/illustrations-animated/EmptyDefaultDarkSvgAnimated';
import Breadcrumbs from '../../../common/Breadcrumbs';
import PassTemplatesHeader from './PassTemplatesHeader';
import EmptyState from '../../../common/EmptyState';
import { Link as RouterLink } from 'react-router-dom';
import PassTemplatesListItem from './PassTemplatesListItem';
import { useCreateTemplateAction } from './hooks/useCreateTemplateAction';
import PrebuiltTemplatePickerModal from './modals/PrebuiltTemplatePickerModal';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';
import LockedActionButton from '../../common/LockedActionButton';

const PassTemplatesList = ({
  items,
  isLoading,
  isLoadingMore,
  hasMore,
  loadMore,
  filtersApplied,
  inputSearch,
  setInputSearch,
}: {
  items: any[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  filtersApplied: boolean;
  inputSearch: string;
  setInputSearch: (input: string) => void;
}) => {
  const modeEmptySvgIcon = useColorModeValue(
    <EmptyDefaultSvgAnimated />,
    <EmptyDefaultDarkSvgAnimated />
  );
  const { shouldRender, components } = useOrganizationStatus(modeEmptySvgIcon);
  const isReadOnly = useIsReadOnly();
  const {
    prebuiltEnabled,
    isPickerOpen,
    openPicker,
    closePicker,
    startFromPreset,
    startFromBlank,
  } = useCreateTemplateAction();

  const breadcrumbLinks = [
    {
      linkName: 'Home',
      linkUrl: '/',
    },
    {
      linkName: 'pass templates',
      linkUrl: null,
    },
  ];

  const totalNumberOfTemplatePreviews = items.length;

  return (
    <Container display="flex" flexDir="column" flex="1" pb={6}>
      <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />
      <PassTemplatesHeader
        inputSearch={inputSearch}
        setInputSearch={setInputSearch}
      />
      {components}
      {totalNumberOfTemplatePreviews > 0 && (
        <>
          <Grid
            templateColumns={{
              base: 'repeat(auto-fill, 343px)',
              xl: 'repeat(auto-fill, 330px)',
              '2xl': 'repeat(auto-fill, 343px)',
            }}
            justifyContent={{
              base: 'center',
              xl: totalNumberOfTemplatePreviews > 2 ? 'center' : 'flex-start',
            }}
            columnGap={16}
            rowGap={16}
          >
            {items.map((previewData) => {
              return (
                <GridItem key={previewData.uuid} borderRadius="10px">
                  <PassTemplatesListItem previewData={previewData} />
                </GridItem>
              );
            })}
          </Grid>
          {hasMore && (
            <Box mt="30px" textAlign="center">
              <Button
                aria-label="Load more templates"
                variant="text"
                size="sm"
                rightIcon={<ChevronDownIcon boxSize="20px" />}
                isLoading={isLoadingMore}
                onClick={(e) => {
                  e.preventDefault();
                  loadMore();
                }}
              >
                Load more templates
              </Button>
            </Box>
          )}
        </>
      )}
      {isLoading && shouldRender && (
        <>
          <Spinner
            width="52px"
            height="52px"
            thickness="4px"
            speed="0.65s"
            emptyColor="quinaryBackground"
            mt={4}
            mx="auto"
          />
        </>
      )}
      {shouldRender &&
        !isLoading &&
        totalNumberOfTemplatePreviews === 0 &&
        !filtersApplied && (
          <EmptyState
            iconSvgAnimated={modeEmptySvgIcon}
            heading="There are no Pass templates yet."
            text="Click on the button below and start creating&nbsp;pass&nbsp;templates."
          >
            {isReadOnly ? (
              <LockedActionButton reason="create-templates" size="sm">
                Create pass template
              </LockedActionButton>
            ) : (
              <Button
                aria-label="Create pass template"
                size="sm"
                {...(prebuiltEnabled
                  ? { onClick: openPicker }
                  : { as: RouterLink, to: '/pass-templates/create' })}
              >
                Create pass template
              </Button>
            )}
          </EmptyState>
        )}
      {shouldRender &&
        !isLoading &&
        totalNumberOfTemplatePreviews === 0 &&
        filtersApplied && (
          <EmptyState
            iconSvgAnimated={modeEmptySvgIcon}
            heading="No results found."
            text="Try adjusting your filters to find the pass templates that you are looking for."
          />
        )}
      {prebuiltEnabled && (
        <PrebuiltTemplatePickerModal
          isOpen={isPickerOpen}
          onClose={closePicker}
          onSelectPreset={startFromPreset}
          onSelectBlank={startFromBlank}
        />
      )}
    </Container>
  );
};

export default PassTemplatesList;
